/**
 * Keyboard and mouse input handler — tracks key state, mouse position
 * in both screen and world space, build mode toggles, and click events.
 */

export interface InputState {
  keys: Set<string>;
  keysJustPressed: Set<string>;
  mouseX: number;
  mouseY: number;
  mouseDown: boolean;
  mouseButton: number;
  mouseWorldX: number;
  mouseWorldY: number;
  wheelDelta: number;
  clicked: boolean;
  clickScreenX: number;
  clickScreenY: number;
}

const TRACKED_KEYS = [
  'w', 'a', 's', 'd',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
  'Shift', ' ', 'b', 'Escape', 'g', 'B', 'G', 't', 'T',
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '_',
];

export class InputMouseKeyboardHandler {
  state: InputState = {
    keys: new Set(),
    keysJustPressed: new Set(),
    mouseX: 0,
    mouseY: 0,
    mouseDown: false,
    mouseButton: 0,
    mouseWorldX: 0,
    mouseWorldY: 0,
    wheelDelta: 0,
    clicked: false,
    clickScreenX: 0,
    clickScreenY: 0,
  };

  private canvas: HTMLCanvasElement;
  private prevKeys: Set<string> = new Set();

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;

    window.addEventListener('keydown', (e) => {
      if (TRACKED_KEYS.includes(e.key)) {
        e.preventDefault();
        this.state.keys.add(e.key);
      }
    });

    window.addEventListener('keyup', (e) => {
      this.state.keys.delete(e.key);
    });

    this.canvas.addEventListener('mousedown', (e) => {
      this.state.mouseDown = true;
      this.state.mouseButton = e.button;
    });

    this.canvas.addEventListener('mouseup', (e) => {
      this.state.mouseDown = false;
      // Left-click triggers a "clicked" event for one frame
      if (e.button === 0) {
        const rect = this.canvas.getBoundingClientRect();
        this.state.clicked = true;
        this.state.clickScreenX = e.clientX - rect.left;
        this.state.clickScreenY = e.clientY - rect.top;
      }
    });

    this.canvas.addEventListener('mousemove', (e) => {
      const rect = this.canvas.getBoundingClientRect();
      this.state.mouseX = e.clientX - rect.left;
      this.state.mouseY = e.clientY - rect.top;
    });

    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.state.wheelDelta += e.deltaY > 0 ? 1 : e.deltaY < 0 ? -1 : 0;
    });

    this.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });
  }

  update(): void {
    // Compute keys just pressed this frame
    this.state.keysJustPressed.clear();
    for (const k of this.state.keys) {
      if (!this.prevKeys.has(k)) {
        this.state.keysJustPressed.add(k);
      }
    }
    this.prevKeys = new Set(this.state.keys);

    // Reset per-frame accumulators
    this.state.wheelDelta = 0;
    this.state.clicked = false;
  }
}
